import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadDunningByCobranca } from 'services/regua-cobranca';
import { EmptyMessage, Load } from 'ui/styled';
import { normalizeResponse } from 'utils';
import { formatPhone, parseCurrency } from 'utils/parsers';
import {
  FiltroButton,
  FiltroContainer,
  FiltroLabel,
  FiltroLinha,
} from '../../styled';
import {
  CampoItem,
  CampoLabel,
  CamposContainer,
  CampoValue,
  CardHeader,
  DisparoCard,
  ErroNota,
  ListaContainer,
  ListaFallback,
  MotivoLabel,
  MotivoNota,
  PassoTag,
  PassoTitulo,
  ReguaHint,
  ReguaTopo,
  StatusBadge,
} from './styled';

const PAGE_SIZE = 200;

// A regua e uma fila: um job monta os disparos de manha gravando a data em que
// cada mensagem deve sair, outro envia o que esta agendado para hoje ou para
// tras. Por isso a mesma lista traz o que ja foi enviado e o que ainda vai
// sair — o que separa um do outro e o status.
//
// `aceito` NAO e entrega confirmada: o provedor aceita ate numero invalido, e
// quem promove para entregue ou rebaixa para erro e a conciliacao. Mostrar
// "entregue" ja no aceito mentiria para o operador, entao ele fica com cor de
// atencao e rotulo proprio.
const STATUS_META = {
  agendado: { label: 'Programado', color: '#2670D1', group: 'scheduled' },
  enviando: { label: 'Enviando agora', color: '#FF9500', group: 'sent' },
  aceito: {
    label: 'Aceito — entrega não confirmada',
    color: '#FF9500',
    group: 'sent',
  },
  enviado: { label: 'Enviado', color: '#00A82A', group: 'sent' },
  entregue: { label: 'Entregue', color: '#00A82A', group: 'sent' },
  lida: { label: 'Lida pelo cliente', color: '#00A82A', group: 'sent' },
  erro: {
    label: 'Falhou — vai tentar de novo',
    color: '#FF3B30',
    group: 'retry',
  },
  sem_telefone: {
    label: 'Sem telefone — vai tentar de novo',
    color: '#FF3B30',
    group: 'retry',
  },
  cancelado: { label: 'Não será enviado', color: '#a4a4a4', group: 'canceled' },
};

const statusMeta = (status) =>
  STATUS_META[status] || {
    label: status || '-',
    color: '#a4a4a4',
    group: 'other',
  };

// O passo da regua com o sinal preservado: D-3 e aviso previo, D0 e no dia do
// vencimento, D+7 e uma semana de atraso.
const passoLabel = (dias) => {
  const numero = Number(dias);
  if (!Number.isFinite(numero)) {
    return 'Passo';
  }
  if (numero === 0) {
    return 'D0';
  }
  return numero > 0 ? `D+${numero}` : `D${numero}`;
};

const formatDate = (value) =>
  value ? moment(value).format('DD/MM/YYYY') : null;

const formatDateTime = (value) =>
  value ? moment(value).format('DD/MM/YYYY HH:mm') : null;

const FILTERS = [
  { id: 'all', title: 'Todos' },
  { id: 'scheduled', title: 'Programados' },
  { id: 'sent', title: 'Enviados' },
  { id: 'retry', title: 'Retentativa' },
  { id: 'canceled', title: 'Cancelados' },
];

const Campo = ({ label, value }) =>
  value === null || value === undefined || value === '' ? null : (
    <CampoItem>
      <CampoLabel>{label}</CampoLabel>
      <CampoValue>{value}</CampoValue>
    </CampoItem>
  );

// Aba Régua do Histórico da Cobrança: os disparos da régua do contrato, o que
// ja saiu e o que ainda vai sair. Sempre no escopo do contrato — o filtro de
// escopo da linha do tempo nao vale aqui, porque a fila e montada por
// contrato. A listagem e somente leitura: a fila e escrita apenas pelo motor
// da regua, que responde 403 a POST/PUT/DELETE.
export const Regua = ({ cobrancaId, onTotal }) => {
  const [loading, setLoading] = useState(false);
  const [dispatches, setDispatches] = useState([]);
  const [filter, setFilter] = useState('all');

  const init = useCallback(async () => {
    if (!cobrancaId) {
      setDispatches([]);
      return;
    }
    setLoading(true);
    try {
      const result = await ReadDunningByCobranca(
        cobrancaId,
        `&sort=data_prevista:asc&pagination[pageSize]=${PAGE_SIZE}`
      );
      setDispatches(normalizeResponse(result));
    } catch (err) {
      console.error('Erro ao carregar a régua da cobrança:', err);
      toast.error('Erro ao carregar os disparos da régua');
    } finally {
      setLoading(false);
    }
  }, [cobrancaId]);

  useEffect(() => init(), [init]);

  useEffect(() => onTotal?.(dispatches.length), [dispatches, onTotal]);

  const contagem = useMemo(
    () =>
      (dispatches || []).reduce((acc, item) => {
        const grupo = statusMeta(item?.status).group;
        return { ...acc, [grupo]: (acc[grupo] || 0) + 1 };
      }, {}),
    [dispatches]
  );

  const visiveis = useMemo(
    () =>
      (dispatches || []).filter(
        (item) => filter === 'all' || statusMeta(item?.status).group === filter
      ),
    [dispatches, filter]
  );

  // O proximo disparo e o primeiro ainda programado; a lista ja chega ordenada
  // por data_prevista crescente.
  const proximo = useMemo(
    () => (dispatches || []).find((item) => item?.status === 'agendado'),
    [dispatches]
  );

  return (
    <>
      <ReguaTopo>
        <FiltroLinha>
          <FiltroLabel>Status</FiltroLabel>
          <FiltroContainer>
            {FILTERS.map((item) => {
              const total =
                item.id === 'all'
                  ? dispatches?.length || 0
                  : contagem[item.id] || 0;
              return (
                <FiltroButton
                  key={item.id}
                  active={filter === item.id}
                  onClick={() => setFilter(item.id)}
                >
                  {item.title} ({total})
                </FiltroButton>
              );
            })}
          </FiltroContainer>
        </FiltroLinha>
        <ReguaHint>
          Próximo disparo:{' '}
          {formatDate(proximo?.data_prevista) || 'nenhum programado'}
        </ReguaHint>
      </ReguaTopo>

      {loading ? (
        <ListaFallback>
          <Load />
        </ListaFallback>
      ) : !visiveis.length ? (
        <ListaFallback>
          <EmptyMessage>
            {dispatches?.length
              ? 'Nenhum disparo neste filtro.'
              : // Fila vazia nao e erro: ela e montada pelo agendador diario
                // e na importacao da carteira.
                'Nenhum disparo programado ainda — a fila é montada pelo agendador diário.'}
          </EmptyMessage>
        </ListaFallback>
      ) : (
        <ListaContainer>
          {visiveis.map((item) => {
            const meta = statusMeta(item?.status);
            const enviado = !!item?.data_envio;
            return (
              <DisparoCard key={item?.chave || item?.id} $color={meta.color}>
                <CardHeader>
                  <PassoTag $color={meta.color}>
                    {passoLabel(item?.dias)}
                  </PassoTag>
                  <PassoTitulo>
                    {item?.observacao || 'Disparo da régua'}
                  </PassoTitulo>
                  <StatusBadge $color={meta.color}>{meta.label}</StatusBadge>
                </CardHeader>
                <CamposContainer>
                  {/* Enquanto nao saiu, a data que vale e a prevista:
                      data_envio nulo e o normal do agendado, nao falha. */}
                  <Campo
                    label={enviado ? 'Enviado em' : 'Sai em'}
                    value={
                      enviado
                        ? formatDateTime(item?.data_envio)
                        : formatDate(item?.data_prevista) || '-'
                    }
                  />
                  <Campo label="Parcela" value={item?.titulo?.parcela} />
                  <Campo
                    label="Valor"
                    value={
                      item?.titulo?.valor === undefined ||
                      item?.titulo?.valor === null
                        ? null
                        : parseCurrency(item.titulo.valor)
                    }
                  />
                  <Campo
                    label="Vencimento"
                    value={formatDate(
                      item?.titulo?.data_vencimento || item?.data_vencimento
                    )}
                  />
                  <Campo
                    label="Telefone"
                    value={formatPhone(null, item?.telefone)}
                  />
                  <Campo
                    label="Entregue em"
                    value={formatDateTime(item?.data_entrega)}
                  />
                  <Campo
                    label="Lida em"
                    value={formatDateTime(item?.data_leitura)}
                  />
                </CamposContainer>
                {/* O mesmo campo `erro` carrega dois sentidos: no cancelado
                    ele e o motivo de o aviso nao ter saido — "Contrato
                    enviado para Execucao" —, e nao uma falha de envio. */}
                {!item?.erro ? null : meta.group === 'canceled' ? (
                  <MotivoNota>
                    <MotivoLabel>Motivo</MotivoLabel>
                    {item.erro}
                  </MotivoNota>
                ) : (
                  <ErroNota>{item.erro}</ErroNota>
                )}
              </DisparoCard>
            );
          })}
        </ListaContainer>
      )}
    </>
  );
};
